import React, { useState } from "react";
import { ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SOCIAL_OPTIONS = [
  { id: "homebody", label: "Homebody", description: "Prefer quiet nights in" },
  { id: "moderate", label: "Balanced", description: "Mix of social and alone time" },
  { id: "very_social", label: "Social Butterfly", description: "Love being around people" },
];

const ACTIVITIES = [
  { id: "hiking", label: "Hiking / Nature" },
  { id: "fitness", label: "Fitness / Gym" },
  { id: "coffee", label: "Coffee Dates" },
  { id: "movies", label: "Movies / Shows" },
  { id: "reading", label: "Reading" },
  { id: "cooking", label: "Cooking" },
  { id: "music", label: "Live Music" },
  { id: "art", label: "Art / Museums" },
  { id: "gaming", label: "Gaming" },
  { id: "sports", label: "Sports" },
  { id: "volunteering", label: "Volunteering" },
  { id: "meditation", label: "Meditation / Yoga" },
  { id: "travel", label: "Travel" },
  { id: "pets", label: "Pet Activities" },
];

const WORK_LIFE_OPTIONS = [
  { id: "work_focused", label: "Career-focused", description: "Work is a priority right now" },
  { id: "balanced", label: "Balanced", description: "Equal focus on work and personal life" },
  { id: "life_focused", label: "Life-focused", description: "Personal time comes first" },
];

const LifestyleStep = ({ onNext }) => {
  const [socialEnvironment, setSocialEnvironment] = useState("moderate");
  const [activities, setActivities] = useState([]);
  const [workLifeBalance, setWorkLifeBalance] = useState("balanced");
  const [submitting, setSubmitting] = useState(false);

  const toggleActivity = (activityId) => {
    setActivities(prev =>
      prev.includes(activityId)
        ? prev.filter(a => a !== activityId)
        : [...prev, activityId]
    );
  };

  const handleSubmit = async () => {
    if (activities.length < 2) {
      toast.error("Please select at least 2 activities");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitLifestyle({
        social_environment: socialEnvironment,
        sober_activities: activities,
        work_life_balance: workLifeBalance
      });
      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Your Lifestyle</h2>
      <p className="text-gray-600 mb-6">What do you enjoy doing clean and sober?</p>

      {/* Social Environment */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Social style
        </label>
        <div className="space-y-2">
          {SOCIAL_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setSocialEnvironment(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                socialEnvironment === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium ${socialEnvironment === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-sm">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Clean/Sober Activities */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Clean/Sober activities you enjoy (select at least 2)
        </label>
        <div className="grid grid-cols-2 gap-2">
          {ACTIVITIES.map(activity => (
            <button
              key={activity.id}
              onClick={() => toggleActivity(activity.id)}
              className={`px-3 py-2.5 rounded-xl border-2 text-left text-sm transition-all flex items-center justify-between ${
                activities.includes(activity.id)
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {activity.label}
              {activities.includes(activity.id) && (
                <Check className="w-4 h-4 text-[#1C3A7A]" />
              )}
            </button>
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-2">
          {activities.length} selected
        </p>
      </div>

      {/* Work-Life Balance */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Work-life balance
        </label>
        <div className="space-y-2">
          {WORK_LIFE_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setWorkLifeBalance(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                workLifeBalance === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className={`font-medium ${workLifeBalance === option.id ? 'text-[#1C3A7A]' : 'text-gray-900'}`}>
                {option.label}
              </p>
              <p className="text-gray-500 text-sm">{option.description}</p>
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || activities.length < 2}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default LifestyleStep;
